import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import axios from 'axios';
import { API_ENDPOINTS, STATUS_OPTIONS, SHIFT_OPTIONS } from '../../config';
import { PRODUCTS, GRADES, BRANDS, PACK_SIZES, PACK_STYLES } from '../../utils/DROPDOWN_OPTIONS';

// ✅ FIXED: Auth helper function
const getAuthConfig = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    },
    withCredentials: true
  };
};

function EditEntryModal({ open, onClose, onSuccess, entryData, user }) {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reason, setReason] = useState('');
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (entryData) {
      setFormData({
        ...entryData,
        production_date: entryData.production_date ? entryData.production_date.split('T')[0] : '',
      });
    }
  }, [entryData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError('Please provide a reason for this edit');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('Submitting direct edit (Phase 1b: approval flow removed)...');

      // ✅ Phase 1b Batch 2: Direct PUT to inventory.php (no more approval workflow)
      // Email notification fires automatically from server side via ENTRY_EDITED event
      const response = await axios.put(
        `${API_ENDPOINTS.INVENTORY}`,
        {
          ...formData,
          id: entryData.id,
          reason: reason,
        },
        getAuthConfig()
      );

      console.log('Edit response:', response.data);

      if (response.data.success) {
        setSuccessMessage(`✅ Entry updated successfully. Notification email has been sent to configured recipients.`);
        setShowSuccessSnackbar(true);

        // Close modal after showing success message
        setTimeout(() => {
          onClose();
          setReason('');
          if (onSuccess) onSuccess();
        }, 2000);
      } else {
        setError(response.data.error || 'Failed to update entry');
      }
    } catch (err) {
      console.error('Error updating entry:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to update entry';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#ff9800', color: 'white' }}>
          ✏️ Edit Inventory Entry
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Alert severity="info" sx={{ mb: 3 }}>
            📋 This edit will be applied immediately. A notification email with the change summary will be sent to all configured recipients.
          </Alert>

          <Grid container spacing={2}>
            {/* Row 1 */}
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                fullWidth
                label="Status"
                value={formData.status || ''}
                onChange={(e) => handleChange('status', e.target.value)}
                size="small"
              >
                {STATUS_OPTIONS.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                type="date"
                label="Production Date"
                value={formData.production_date || ''}
                onChange={(e) => handleChange('production_date', e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                fullWidth
                label="Shift"
                value={formData.shift || ''}
                onChange={(e) => handleChange('shift', e.target.value)}
                size="small"
              >
                {SHIFT_OPTIONS.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Product ID"
                value={formData.product_id || ''}
                onChange={(e) => handleChange('product_id', e.target.value)}
                size="small"
              />
            </Grid>

            {/* Row 2 */}
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="NAV ID (Auto-generated)"
                value={formData.nav_id || 'NOT AVAILABLE'}
                size="small"
                disabled
                helperText="No matching NAV ID found"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                fullWidth
                label="Pack Style **"
                value={formData.pack_style || ''}
                onChange={(e) => handleChange('pack_style', e.target.value)}
                size="small"
              >
                {PACK_STYLES.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                fullWidth
                label="Product **"
                value={formData.product || ''}
                onChange={(e) => handleChange('product', e.target.value)}
                size="small"
              >
                {PRODUCTS.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                fullWidth
                label="Grade **"
                value={formData.grade || ''}
                onChange={(e) => handleChange('grade', e.target.value)}
                size="small"
              >
                {GRADES.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Row 3 */}
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                fullWidth
                label="Pack Size"
                value={formData.pack_size || ''}
                onChange={(e) => handleChange('pack_size', e.target.value)}
                size="small"
              >
                {PACK_SIZES.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                fullWidth
                label="Brand"
                value={formData.brand || ''}
                onChange={(e) => handleChange('brand', e.target.value)}
                size="small"
              >
                {BRANDS.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                type="number"
                label="Cartons"
                value={formData.cartons || 0}
                onChange={(e) => handleChange('cartons', parseInt(e.target.value) || 0)}
                size="small"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                type="number"
                label="Loose Bags"
                value={formData.loose_bags || 0}
                onChange={(e) => handleChange('loose_bags', parseInt(e.target.value) || 0)}
                size="small"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                type="number"
                label="Loose KGs"
                value={formData.loose_kgs || 0}
                onChange={(e) => handleChange('loose_kgs', parseFloat(e.target.value) || 0)}
                size="small"
                inputProps={{ step: 0.01 }}
              />
            </Grid>

            {/* Row 4 */}
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Total KGs (Auto-Calculated)"
                value={formData.total_kgs || 0}
                size="small"
                disabled
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Total Pounds (Auto-Calculated)"
                value={formData.total_pounds || 0}
                size="small"
                disabled
              />
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                label="Lot No"
                value={formData.lot_no || ''}
                onChange={(e) => handleChange('lot_no', e.target.value)}
                size="small"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                label="Trace ID"
                value={formData.trace_id || ''}
                onChange={(e) => handleChange('trace_id', e.target.value)}
                size="small"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                label="Day Code"
                value={formData.day_code || ''}
                onChange={(e) => handleChange('day_code', e.target.value)}
                size="small"
              />
            </Grid>

            {/* Row 5 */}
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Pallet ID"
                value={formData.pallet_id || ''}
                onChange={(e) => handleChange('pallet_id', e.target.value)}
                size="small"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="CS1"
                value={formData.cs1 || ''}
                onChange={(e) => handleChange('cs1', e.target.value)}
                size="small"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="CS2"
                value={formData.cs2 || ''}
                onChange={(e) => handleChange('cs2', e.target.value)}
                size="small"
              />
            </Grid>

            {/* Remarks */}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Remarks"
                value={formData.remarks || ''}
                onChange={(e) => handleChange('remarks', e.target.value)}
                size="small"
              />
            </Grid>

            {/* Reason - REQUIRED */}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Reason for Edit *"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                error={!reason.trim()}
                helperText="Please explain why you need to make this edit (Required)"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff3e0'
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={onClose}
            disabled={loading}
            startIcon={<span>✖</span>}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !reason.trim()}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <span>📝</span>}
            sx={{
              bgcolor: '#ff9800',
              '&:hover': { bgcolor: '#f57c00' }
            }}
          >
            {loading ? 'Submitting...' : 'Submit for Approval'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar - Top Center */}
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowSuccessSnackbar(false)} 
          severity="success" 
          variant="filled"
          sx={{ 
            width: '100%', 
            fontSize: '1.1rem',
            fontWeight: 'bold'
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default EditEntryModal;